import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as THREE from 'three'
import {
  Path,
  Shape,
  ExtrudeGeometry,
  EdgesGeometry,
  LineSegments,
  LineBasicMaterial,
  MeshLambertMaterial,
} from 'three'
import { getApartmentGroup, getMainApartmentId } from './ApartmentBuilding'

//
// ————— DATA TYPES —————
//

interface Vec2 {
  x: number;
  y: number;
}

interface ApartmentFootprint {
  id: string;
  outline: Vec2[];
  holes?: Vec2[][];
  availability?: boolean;
}

export interface FloorData {
  id: string;
  height: number;
  apartments: ApartmentFootprint[];
}

interface Overlay3DProps {
  floorsData: FloorData[];
  rotationDeg: number;
  frameIndex?: number;
  opacity?: number;
  cellSize?: number; // scale factor: how many world‐units per "coordinate unit"
  tiltDeg?: number;
  fovDeg?: number;
  xOffset?: number;
  yOffset?: number;
  zOffset?: number;
  gridWidth?: number;
  gridHeight?: number;
  minWidth?: number;
  minHeight?: number;

  // ← NEW: Called whenever the mouse is over an apartment (or `null` if none)
  onApartmentHover?: (
    info: {
      aptId: string;
      floorIndex: number;
      clientX: number;
      clientY: number;
    } | null
  ) => void;

  // ← NEW: Called whenever an apartment is clicked
  onApartmentClick?: (info: { aptId: string; floorIndex: number }) => void;

  // ← Selected apartment to preserve across component remounts
  selectedApartment?: { aptId: string; floorIndex: number } | null;

  // ← Hovered apartment from external source (apartment list)
  hoveredApartment?: { id: string; floor: number } | null;
}

function isInRange (frameIndex: number, start: number, end: number): boolean {
  if (start <= end) {
    return frameIndex >= start && frameIndex <= end
  }

  return frameIndex >= start || frameIndex <= end
}

export const Overlay3D = forwardRef<any, Overlay3DProps>(
  (
    {
      floorsData,
      rotationDeg,
      frameIndex = 0,
      opacity = 0.5,
      cellSize = 0.65,
      tiltDeg = 12,
      fovDeg = 10,
      xOffset = 0,
      yOffset = 0,
      zOffset = -1.30,
      gridWidth = 8,
      gridHeight = 10,
      minWidth = 300,
      minHeight = 200,
      onApartmentHover, // ← NEW
      onApartmentClick, // ← NEW
      selectedApartment, // ← NEW
      hoveredApartment, // ← NEW
    },
    ref
  ) => {
    const mountRef = useRef<HTMLDivElement | null>(null)

    // Scene / camera / renderer refs
    const sceneRef = useRef<THREE.Scene>()
    const cameraRef = useRef<THREE.PerspectiveCamera>()
    const rendererRef = useRef<THREE.WebGLRenderer>()
    const buildingGroupRef = useRef<THREE.Group>()
    const apartmentMeshesRef = useRef<Map<string, THREE.Mesh>>(new Map())

    // Track hover/selection
    const hoveredApartmentRef = useRef<string | null>(null)
    const selectedAptRef = useRef<{ aptId: string; floorIndex: number } | null>(
      null
    )
    const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster())
    const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2())
    const rotationRef = useRef<number>(rotationDeg)
    const animationFrameIdRef = useRef<number | null>(null)

    const resizeObserverRef = useRef<ResizeObserver | null>(null)

    const centerOffsetX = gridWidth / 2
    const centerOffsetY = gridHeight / 2

    const BASE_COLOR = 0x303030
    const HOVER_COLOR = 0xecf5ff
    const SELECT_COLOR = 0x0080ff

    useEffect(() => {
      rotationRef.current = -rotationDeg
    }, [rotationDeg])

    // Sync selectedApartment prop with internal ref
    useEffect(() => {
      selectedAptRef.current = selectedApartment || null
    }, [selectedApartment])

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        deselectApartment: () => {
          if (selectedAptRef.current) {
            // Clear selection highlighting
            const { aptId, floorIndex } = selectedAptRef.current
            const group = getApartmentGroup(aptId, floorIndex)
            group.forEach((groupAptId) => {
              const key = `${floorIndex}_${groupAptId}`
              const mesh = apartmentMeshesRef.current.get(key)
              if (mesh) {
                const materials = mesh.material as MeshLambertMaterial[]
                if (Array.isArray(materials)) {
                  materials[0].color.set(BASE_COLOR) // top face
                  materials[1].color.set(BASE_COLOR) // bottom face
                  materials[2].color.set(BASE_COLOR) // side faces
                } else {
                  (materials as MeshLambertMaterial).color.set(BASE_COLOR)
                }
              }
            })
            selectedAptRef.current = null
          }
        },
      }),
      []
    )

    // Responsive resize
    const handleResize = useCallback(() => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current)
        return
      const containerWidth = mountRef.current.clientWidth
      const containerHeight = mountRef.current.clientHeight
      const width = Math.max(containerWidth, minWidth)
      const height = Math.max(containerHeight, minHeight)
      rendererRef.current.setSize(width, height)
      cameraRef.current.aspect = width / height
      cameraRef.current.updateProjectionMatrix()
    }, [minWidth, minHeight])

    // Reposition group when offsets change
    useEffect(() => {
      if (!buildingGroupRef.current) return
      const rotRad = THREE.MathUtils.degToRad(rotationDeg)
      const dx = Math.cos(rotRad) * xOffset - Math.sin(rotRad) * yOffset
      const dy = Math.sin(rotRad) * xOffset + Math.cos(rotRad) * yOffset
      buildingGroupRef.current.position.set(dx, dy, zOffset)
    }, [xOffset, yOffset, zOffset, rotationDeg])

    // Initialize renderer only once
    useEffect(() => {
      if (!mountRef.current || rendererRef.current) return

      const mountDiv = mountRef.current
      const containerWidth = mountDiv.clientWidth
      const containerHeight = mountDiv.clientHeight
      const width = Math.max(containerWidth, minWidth)
      const height = Math.max(containerHeight, minHeight)

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      })
      renderer.setSize(width, height)
      renderer.setPixelRatio(window.devicePixelRatio)
      renderer.setClearColor(0x000000, 0) // transparent
      renderer.domElement.style.background = 'transparent'
      rendererRef.current = renderer
      mountDiv.appendChild(renderer.domElement)

      return () => {
        if (rendererRef.current) {
          rendererRef.current.dispose()
          if (mountDiv.contains(rendererRef.current.domElement)) {
            mountDiv.removeChild(rendererRef.current.domElement)
          }
          rendererRef.current = undefined
        }
      }
    }, [minWidth, minHeight])

    // Setup scene and camera
    useEffect(() => {
      if (!mountRef.current || !rendererRef.current) return

      // Cancel any existing animation frame before starting new one
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current)
        animationFrameIdRef.current = null
      }

      //
      // ————— SCENE / CAMERA SETUP —————
      //
      const scene = new THREE.Scene()
      sceneRef.current = scene

      const mountDiv = mountRef.current
      const containerWidth = mountDiv.clientWidth
      const containerHeight = mountDiv.clientHeight
      const width = Math.max(containerWidth, minWidth)
      const height = Math.max(containerHeight, minHeight)

      const camera = new THREE.PerspectiveCamera(
        fovDeg,
        width / height,
        0.1,
        1000
      )
      const tiltRad = THREE.MathUtils.degToRad(tiltDeg)

      // Calculate distance based on FOV to maintain consistent view size
      // For a reference: at 45° FOV, distance should be ~50
      // Use inverse relationship: smaller FOV needs larger distance
      const referenceFOV = 8 // degrees
      const referenceDistance = 50
      const distance = referenceDistance * (referenceFOV / fovDeg)
      camera.position.set(
        0,
        -Math.cos(tiltRad) * distance,
        Math.sin(tiltRad) * distance
      )
      camera.lookAt(0, 0, 0)
      camera.up.set(0, 0, 1)
      cameraRef.current = camera

      if (window.ResizeObserver) {
        resizeObserverRef.current = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            handleResize()
          })
        })
        resizeObserverRef.current.observe(mountDiv)
      }
      window.addEventListener('resize', handleResize)

      const ambientLight = new THREE.AmbientLight(0xffffff, 1)
      scene.add(ambientLight)
      const dirLight = new THREE.DirectionalLight(0xffffff, 1)
      dirLight.position.set(50, 50, 100)
      scene.add(dirLight)

      const buildingGroup = new THREE.Group()
      // initial offset & rotation
      {
        const initialRotRad = THREE.MathUtils.degToRad(rotationDeg)
        const initDx =
          Math.cos(initialRotRad) * xOffset - Math.sin(initialRotRad) * yOffset
        const initDy =
          Math.sin(initialRotRad) * xOffset + Math.cos(initialRotRad) * yOffset
        buildingGroup.position.set(initDx, initDy, zOffset)
      }
      buildingGroupRef.current = buildingGroup
      scene.add(buildingGroup)

      const borderMaterial = new LineBasicMaterial({
        color: 0xffffff,
        linewidth: 2,
      })

      //
      // ————— BUILD FLOORS & APARTMENTS —————
      //
      let cumulativeHeight = 0

      floorsData.forEach((floor, fIndex) => {
        floor.apartments.forEach((apt) => {
          // Skip unavailable apartments
          if (apt.availability === false) {
            return
          }

          if ((apt.id === 'A1' || apt.id === 'A2' || apt.id === 'B1' || apt.id === 'B2' || apt.id === 'B3' || apt.id === 'C1' || apt.id === 'C2') && isInRange(frameIndex, 17, 79)) {
            return
          }

          if ((apt.id === 'A21' || apt.id === 'A3' || apt.id === 'B31' || apt.id === 'B4' || apt.id === 'C21') && isInRange(frameIndex, 40, 100)) {
            return
          }

          // 1) Build a THREE.Shape from apt.outline
          const shape = new Shape()
          const pts = apt.outline.map((p) => ({
            x: (p.x - centerOffsetX) * cellSize,
            y: (p.y - centerOffsetY) * cellSize,
          }))
          shape.moveTo(pts[0].x, pts[0].y)
          for (let i = 1; i < pts.length; i++) {
            shape.lineTo(pts[i].x, pts[i].y)
          }
          shape.lineTo(pts[0].x, pts[0].y)

          // 2) Add holes (if any)
          if (apt.holes) {
            apt.holes.forEach((holePts) => {
              const holePath = new Path()
              const hpts = holePts.map((p) => ({
                x: (p.x - centerOffsetX) * cellSize,
                y: (p.y - centerOffsetY) * cellSize,
              }))
              holePath.moveTo(hpts[0].x, hpts[0].y)
              for (let j = 1; j < hpts.length; j++) {
                holePath.lineTo(hpts[j].x, hpts[j].y)
              }
              holePath.lineTo(hpts[0].x, hpts[0].y)
              shape.holes.push(holePath)
            })
          }

          // 3) Extrude
          const extrudeSettings: THREE.ExtrudeGeometryOptions = {
            depth: floor.height,
            bevelEnabled: false,
          }
          const geom3d = new ExtrudeGeometry(shape, extrudeSettings)
          geom3d.translate(0, 0, cumulativeHeight)

          // 4) Create mesh with transparent top and back-face culling
          // We'll use an array of materials: one for sides, one for top/bottom
          const sideMaterial = new MeshLambertMaterial({
            color: BASE_COLOR,
            side: THREE.FrontSide, // Only visible from outside
          })
          const topMaterial = new MeshLambertMaterial({
            color: BASE_COLOR,
            transparent: true,
            opacity: 0.1,
            side: THREE.FrontSide, // Only visible from outside
          })

          // For ExtrudeGeometry, material index 0 = front face, 1 = back face, 2 = sides
          // We want the top (front face) to be transparent
          const materials = [topMaterial, sideMaterial, sideMaterial]
          const aptMesh = new THREE.Mesh(geom3d, materials)
          aptMesh.userData = {
            aptId: apt.id,
            floorIndex: fIndex,
            isApartment: true,
          }
          buildingGroup.add(aptMesh)
          apartmentMeshesRef.current.set(`${fIndex}_${apt.id}`, aptMesh)

          // 5) Draw outline
          const edgesGeo = new EdgesGeometry(geom3d)
          const outline = new LineSegments(edgesGeo, borderMaterial)
          buildingGroup.add(outline)
        })
        cumulativeHeight += floor.height
      })

      //
      // ————— INTERACTION HANDLERS —————
      //
      const updateApartmentColor = (
        aptId: string,
        fIndex: number,
        color: number
      ) => {
        const key = `${fIndex}_${aptId}`
        const mesh = apartmentMeshesRef.current.get(key)
        if (mesh) {
          const materials = mesh.material as MeshLambertMaterial[]
          if (Array.isArray(materials)) {
            // Update all materials but keep top material transparent
            materials[0].color.set(color) // top face - keep transparent
            materials[1].color.set(color) // bottom face
            materials[2].color.set(color) // side faces
          } else {
            // Fallback for single material
            (materials as MeshLambertMaterial).color.set(color)
          }
        }
      }

      // Helper function to highlight all apartments in a group on the specific floor
      const updateGroupColor = (
        aptId: string,
        floorIndex: number,
        color: number
      ) => {
        const group = getApartmentGroup(aptId, floorIndex)
        group.forEach((groupAptId) => {
          // Only update color for apartments in the group on the same floor
          const key = `${floorIndex}_${groupAptId}`
          const mesh = apartmentMeshesRef.current.get(key)
          if (mesh) {
            updateApartmentColor(groupAptId, floorIndex, color)
          }
        })
      }

      const onMouseMove = (event: MouseEvent) => {
        if (
          !cameraRef.current ||
          !rendererRef.current ||
          !buildingGroupRef.current
        )
          return
        const rect = rendererRef.current.domElement.getBoundingClientRect()
        const mx = ((event.clientX - rect.left) / rect.width) * 2 - 1
        const my = -((event.clientY - rect.top) / rect.height) * 2 + 1
        mouseRef.current.set(mx, my)
        raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current)

        // Only intersect objects where userData.isApartment === true
        const hits = raycasterRef.current
          .intersectObjects(buildingGroupRef.current.children, true)
          .filter((h) => (h.object.userData as any).isApartment)

        if (hits.length > 0) {
          const mesh = hits[0].object as THREE.Mesh
          const { aptId, floorIndex } = mesh.userData

          // Reset previous hover group if it wasn't selected
          if (hoveredApartmentRef.current) {
            const parts = hoveredApartmentRef.current.split('_')
            const isInternal = parts[0] === 'internal'
            const prevF = isInternal ? parts[1] : parts[0]
            const prevA = isInternal ? parts[2] : parts[1]

            const isInSelectedGroup =
              selectedAptRef.current &&
              getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(prevA) &&
              selectedAptRef.current.floorIndex === parseInt(prevF)

            if (!isInSelectedGroup) {
              updateGroupColor(prevA, parseInt(prevF), BASE_COLOR)
            }
          }

          // If this specific apt isn't already selected, color the whole group HOVER_COLOR
          const isCurrentlySelected =
            selectedAptRef.current &&
            getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(aptId) &&
            selectedAptRef.current.floorIndex === floorIndex

          if (!isCurrentlySelected) {
            updateGroupColor(aptId, floorIndex, HOVER_COLOR)
          }
          hoveredApartmentRef.current = `internal_${floorIndex}_${aptId}`

          // ← NEW: notify parent about hover (with mouse position) - use main apartment ID
          if (onApartmentHover) {
            onApartmentHover({
              aptId: getMainApartmentId(aptId, floorIndex), // Always show the primary apartment (A1 instead of A12)
              floorIndex,
              clientX: event.clientX,
              clientY: event.clientY,
            })
          }

          return
        }

        // No hits → clear previous hover visuals & notify parent
        if (hoveredApartmentRef.current) {
          const parts = hoveredApartmentRef.current.split('_')
          const isInternal = parts[0] === 'internal'
          const fI = isInternal ? parts[1] : parts[0]
          const aI = isInternal ? parts[2] : parts[1]

          const isInSelectedGroup =
            selectedAptRef.current &&
            getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(aI) &&
            selectedAptRef.current.floorIndex === parseInt(fI)

          if (!isInSelectedGroup) {
            updateGroupColor(aI, parseInt(fI), BASE_COLOR)
          }
          hoveredApartmentRef.current = null
        }
        if (onApartmentHover) {
          onApartmentHover(null)
        }
      }

      const onClick = () => {
        if (!hoveredApartmentRef.current) return
        const parts = hoveredApartmentRef.current.split('_')
        const isInternal = parts[0] === 'internal'
        const fI = isInternal ? parts[1] : parts[0]
        const aI = isInternal ? parts[2] : parts[1]
        const floorIndex = parseInt(fI)
        const aptId = aI

        if (
          selectedAptRef.current &&
          selectedAptRef.current.aptId === aptId &&
          selectedAptRef.current.floorIndex === floorIndex
        ) {
          // Deselect specific apartment
          updateGroupColor(aptId, floorIndex, BASE_COLOR)
          selectedAptRef.current = null
        } else {
          // Clear previous selection
          if (selectedAptRef.current) {
            updateGroupColor(
              selectedAptRef.current.aptId,
              selectedAptRef.current.floorIndex,
              BASE_COLOR
            )
          }
          // Select new specific apartment
          selectedAptRef.current = { aptId, floorIndex }
          updateGroupColor(aptId, floorIndex, SELECT_COLOR)
        }

        // ← NEW: notify parent about the click - use main apartment ID
        if (onApartmentClick) {
          onApartmentClick({ aptId: getMainApartmentId(aptId, floorIndex), floorIndex })
        }
      }

      // Restore selection highlighting after scene rebuild
      if (selectedAptRef.current) {
        updateGroupColor(
          selectedAptRef.current.aptId,
          selectedAptRef.current.floorIndex,
          SELECT_COLOR
        )
      }

      const canvas = rendererRef.current!.domElement
      canvas.addEventListener('mousemove', onMouseMove)
      canvas.addEventListener('click', onClick)

      //
      // ————— RENDER LOOP —————
      //
      const animate = () => {
        animationFrameIdRef.current = requestAnimationFrame(animate)
        if (buildingGroupRef.current) {
          buildingGroupRef.current.rotation.z = THREE.MathUtils.degToRad(
            rotationRef.current
          )
        }
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current)
        }
      }
      animate()

      return () => {
        if (animationFrameIdRef.current !== null) {
          cancelAnimationFrame(animationFrameIdRef.current)
          animationFrameIdRef.current = null
        }
        if (rendererRef.current) {
          const canvas = rendererRef.current.domElement
          canvas.removeEventListener('mousemove', onMouseMove)
          canvas.removeEventListener('click', onClick)
        }
        window.removeEventListener('resize', handleResize)
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect()
          resizeObserverRef.current = null
        }
        // Clear apartment meshes map to prevent memory leaks
        apartmentMeshesRef.current.clear()
      }
    }, [
      floorsData,
      frameIndex,
      rotationDeg,
      tiltDeg,
      fovDeg,
      xOffset,
      yOffset,
      zOffset,
      cellSize,
      gridWidth,
      gridHeight,
      centerOffsetX,
      centerOffsetY,
      handleResize,
      minWidth,
      minHeight,
      onApartmentHover,
      onApartmentClick,
      selectedApartment,
    ])

    // Separate useEffect for handling external hover highlighting (from apartment list)
    // This doesn't rebuild the scene, just updates colors
    useEffect(() => {
      if (!apartmentMeshesRef.current.size) return // Scene not built yet

      const updateGroupColor = (
        aptId: string,
        floorIndex: number,
        color: number
      ) => {
        const group = getApartmentGroup(aptId, floorIndex)
        group.forEach((groupAptId) => {
          const key = `${floorIndex}_${groupAptId}`
          const mesh = apartmentMeshesRef.current.get(key)
          if (mesh) {
            const materials = mesh.material as MeshLambertMaterial[]
            if (Array.isArray(materials)) {
              materials[0].color.set(color) // top face
              materials[1].color.set(color) // bottom face
              materials[2].color.set(color) // side faces
            } else {
              (materials as MeshLambertMaterial).color.set(color)
            }
          }
        })
      }

      if (!hoveredApartment) {
        // Clear external hover highlighting
        if (
          hoveredApartmentRef.current &&
          !hoveredApartmentRef.current.includes('internal_')
        ) {
          const parts = hoveredApartmentRef.current.split('_')
          const fI = parts[0]
          const aI = parts[1]
          const isInSelectedGroup =
            selectedAptRef.current &&
            getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(aI) &&
            selectedAptRef.current.floorIndex === parseInt(fI)

          if (!isInSelectedGroup) {
            updateGroupColor(aI, parseInt(fI), BASE_COLOR)
          }
          hoveredApartmentRef.current = null
        }
        return
      }

      // Highlight apartment from external hover
      const { id, floor } = hoveredApartment
      const floorIndex = floor - 1 // Convert floor number to index

      // Clear previous external hover if it exists and wasn't selected
      if (
        hoveredApartmentRef.current &&
        !hoveredApartmentRef.current.includes('internal_')
      ) {
        const parts = hoveredApartmentRef.current.split('_')
        const prevF = parts[0]
        const prevA = parts[1]
        const isInSelectedGroup =
          selectedAptRef.current &&
          getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(prevA) &&
          selectedAptRef.current.floorIndex === parseInt(prevF)

        if (!isInSelectedGroup) {
          updateGroupColor(prevA, parseInt(prevF), BASE_COLOR)
        }
      }

      // Highlight new apartment if not selected
      const isCurrentlySelected =
        selectedAptRef.current &&
        getApartmentGroup(selectedAptRef.current.aptId, selectedAptRef.current.floorIndex).includes(id) &&
        selectedAptRef.current.floorIndex === floorIndex

      if (!isCurrentlySelected) {
        updateGroupColor(id, floorIndex, HOVER_COLOR)
        hoveredApartmentRef.current = `${floorIndex}_${id}`
      }
    }, [hoveredApartment])

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity,
          minWidth: `${minWidth}px`,
          minHeight: `${minHeight}px`,
        }}
      >
        <div
          ref={mountRef}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            pointerEvents: 'auto',
            minWidth: `${minWidth}px`,
            minHeight: `${minHeight}px`,
          }}
        />
      </div>
    )
  }
)
